/**
 * 腾讯广告 → 红线规则配置（CLAUDE.md §前端 UI 须借鉴 LEO v1.0；本页复用 leo-* 类）。
 *
 * 行为：
 *   - 进入页面：GET /api/v1/tencent/redline-config 拉取全部配置行。
 *   - 按 targeting_type 经 REDLINE_EDITOR_MAP 渲染对应富编辑器卡片
 *     （targeting_type 只读卡片标题 + targeting_name 输入 + 编辑器主体）。
 *   - 保存：PUT /api/v1/tencent/redline-config，body = { rows }。
 *     create_user 保留首次、modify_user 每次更新（后端 SQL 层保证）。
 *   - 空表：显示「初始化默认配置」按钮 → POST /seed 种入 8 行默认。
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError } from "../../services/apiClient";
import {
  type RedlineConfigItem,
  type RedlineConfigRecord,
  getRedlineConfig,
  saveRedlineConfig,
  seedRedlineConfig,
} from "../../services/tencent/redlineConfig";
import { getRedlineEditor } from "../../components/redlineConfig/editorMap";
import { pushToast } from "../../hooks/useToast";
import { useAuth } from "../../contexts/AuthContext";
import { PAGE_KEYS } from "../../config/permissions";

function sig(it: {
  targeting_type: string;
  targeting_id: string;
  targeting_name?: string | null;
  ext_info?: string | null;
}): string {
  return `${it.targeting_type}${it.targeting_id}${it.targeting_name ?? ""}${it.ext_info ?? ""}`;
}

function toItem(r: RedlineConfigRecord): RedlineConfigItem {
  return {
    targeting_type: r.targeting_type,
    targeting_id: r.targeting_id,
    targeting_name: r.targeting_name ?? "",
    ext_info: r.ext_info ?? "",
  };
}

export default function RedlineConfigPage() {
  const { canEdit } = useAuth();
  const canEditPage = canEdit(PAGE_KEYS.TENCENT_REDLINE);
  const [items, setItems] = useState<RedlineConfigItem[]>([]);
  const [loaded, setLoaded] = useState<RedlineConfigRecord[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const initialRef = useRef(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await getRedlineConfig();
      setItems(rows.map(toItem));
      setLoaded(rows);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "拉取红线配置失败";
      pushToast(msg, "error");
      setItems([]);
      setLoaded([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    if (initialRef.current) {
      initialRef.current = false;
    }
  }, [items]);

  const dirty = useMemo(() => {
    if (!loaded) return false;
    return items.map(sig).join("\n") !== loaded.map(sig).join("\n");
  }, [items, loaded]);

  function updateItem(i: number, patch: Partial<RedlineConfigItem>) {
    setItems((prev) => prev.map((it, j) => (j === i ? { ...it, ...patch } : it)));
  }

  async function onSeed() {
    setSeeding(true);
    try {
      const n = await seedRedlineConfig();
      pushToast(
        n > 0 ? `已初始化 ${n} 行默认配置` : "配置已存在，未覆盖（仅空表生效）",
        n > 0 ? "success" : "info",
      );
      await reload();
    } catch (err) {
      pushToast(err instanceof ApiError ? err.message : "初始化失败", "error");
    } finally {
      setSeeding(false);
    }
  }

  async function onSave() {
    setSaving(true);
    try {
      const rows = await saveRedlineConfig({ rows: items });
      setItems(rows.map(toItem));
      setLoaded(rows);
      pushToast("保存成功", "success");
    } catch (err) {
      pushToast(
        err instanceof ApiError
          ? `${err.message}${err.suggestion ? `（${err.suggestion}）` : ""}`
          : "保存失败",
        "error",
      );
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-[13px] text-text-muted">加载中…</div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1200px] px-6 py-7 md:px-10 md:py-9 lg:px-14">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-[1.55rem] font-bold leading-tight text-text-emphasis">
            红线规则配置
          </h1>
          <p className="mt-1 text-[12.5px] text-text-muted">
            腾讯广告红线规则（legal_date / bidlimit / excluded_custom 等），保存到
            auto_tx_redline_config；create_user 保留首次、modify_user 每次更新。
          </p>
        </div>
        <div className="flex items-center gap-2">
          {loaded && loaded.length > 0 ? (
            <span className="leo-tag is-info">已配置 · {loaded.length} 行</span>
          ) : (
            <span className="leo-tag is-warning">尚未配置</span>
          )}
          {dirty ? <span className="leo-tag is-warning">有未保存的修改</span> : null}
        </div>
      </div>

      {!canEditPage ? (
        <div className="mb-4 rounded-md border border-semantic-warning/30 bg-semantic-warning/10 px-3 py-2 text-[12px] text-semantic-warning">
          当前账号为只读，无法编辑该页面配置。
        </div>
      ) : null}

      {items.length === 0 ? (
        <div className="leo-card rounded-xl p-8 text-center">
          <div className="mb-3 text-[12.5px] text-text-muted">
            尚无红线规则配置，可一键初始化 8 行默认配置（来自原文件）。
          </div>
          <button
            type="button"
            onClick={onSeed}
            disabled={seeding || !canEditPage}
            className="leo-cta-primary w-auto px-7"
          >
            {seeding ? "初始化中..." : "初始化默认配置"}
          </button>
        </div>
      ) : (
        <>
          <div className="mb-3 flex items-center justify-between">
            <div className="text-[12.5px] text-text-muted">
              共 <span className="text-text-emphasis">{items.length}</span> 行配置
            </div>
          </div>

          <div className="space-y-3">
            {items.map((it, i) => {
              const Editor = getRedlineEditor(it.targeting_type);
              const rec =
                loaded?.find(
                  (r) =>
                    r.targeting_type === it.targeting_type &&
                    r.targeting_id === it.targeting_id,
                ) ?? null;
              return (
                <div
                  key={it.targeting_type}
                  className="rounded-xl border border-line-strong bg-da-card p-5 shadow-card"
                >
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <div className="font-mono text-[13px] text-text-emphasis">
                      {it.targeting_type}
                    </div>
                    {rec?.modify_user ? (
                      <span className="leo-tag">
                        最近修改：{rec.modify_user} · {rec.updated_time ?? "—"}
                      </span>
                    ) : null}
                  </div>
                  <div className="mb-3 flex items-center gap-2">
                    <span className="whitespace-nowrap text-[12px] text-text-muted">
                      targeting_name：
                    </span>
                    <input
                      type="text"
                      className="leo-input min-w-0 flex-1"
                      readOnly
                      value={it.targeting_name}
                    />
                    <span className="whitespace-nowrap text-[12px] text-text-muted">
                      targeting_name为主键，请不要修改
                    </span>
                  </div>
                  <Editor
                    item={it}
                    onChange={(c) =>
                      updateItem(i, {
                        targeting_id: c.targeting_id,
                        ext_info: c.ext_info,
                      })
                    }
                  />
                </div>
              );
            })}
          </div>

          <div className="mt-7 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => reload()}
              disabled={saving}
              className="leo-btn-ghost"
            >
              重新加载
            </button>
            <button
              type="button"
              onClick={onSave}
              disabled={saving || !canEditPage}
              className="leo-cta-primary w-auto px-7"
            >
              {saving ? "保存中..." : dirty ? "保存配置" : "已保存"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
